import React, { useState, useEffect, useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { Box, Container, Typography, Button } from "@mui/material";
import Header from "../../components/User/Header";
import { useSidebar } from "../../components/SidebarContext";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../../config/api";
import { auth } from "../../config/firebaseConfig";
import SearchBar from "../../components/User/SearchBar";
import SearchTips from "../../components/User/SearchTips";
import ProcessingView from "../../components/User/ProcessingView";
import ResultView from "../../components/User/ResultView";
// QR code generation moved to ClaimReceipt via util

const Home = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const text = "Looking for something?";
  const [displayText, setDisplayText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [numLines, setNumLines] = useState(1);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const typingSpeed = 50;
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { isSidebarOpen } = useSidebar();
  const navigate = useNavigate();
  const [isFlipped, setIsFlipped] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    contactNumber: "",
    description: "",
    additionalNotes: "",
    consent: false
  });
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [qrPayload, setQrPayload] = useState(null);

  const BASE_URL = getApiUrl("").replace(/\/$/, ""); // Remove trailing slash to prevent //match

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAttachClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (!isSearching) {
      let index = 0;
      const interval = setInterval(() => {
        setDisplayText(text.substring(0, index + 1));
        index++;
        if (index === text.length) clearInterval(interval);
      }, typingSpeed);

      return () => clearInterval(interval);
    }
  }, [isSearching, text, typingSpeed]);

  const fetchSuggestions = async (query) => {
    if (query.length > 2) {
      try {
        const response = await fetch(`${BASE_URL}/suggestions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSuggestions(data.suggestions || []);
      } catch (error) {
        console.error("Failed to fetch suggestions", error);
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchValue(suggestion);
    setSuggestions([]);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    fetchSuggestions(value);
  };

  useEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) return;

    const observer = new ResizeObserver(() => {
      const lineHeight = parseFloat(
        window.getComputedStyle(inputElement).lineHeight
      );
      const height = inputElement.scrollHeight;
      setNumLines(Math.max(1, Math.round(height / lineHeight)));
    });

    observer.observe(inputElement);
    return () => observer.disconnect();
  }, [searchValue]);

  const handleSend = async () => {
    if (selectedImage && !searchValue.trim()) {
      // Show error message if trying to submit image without text
      setSearchValue("Please input text description");
      return;
    }

    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const time = now.toTimeString().split(":").slice(0, 2).join(":");
  
    const query = searchValue.trim() ? `${searchValue} ${date} ${time}` : null;
    
    setIsSearching(true);
    setIsProcessing(true);
    
    const formData = new FormData();
    if (query) {
      formData.append("query", query);
    }
    if (selectedImage) {
      formData.append("image", selectedImage);
    }

    try {
      const response = await fetch(`${BASE_URL}/match`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSearchResults(data.matches);
    } catch (error) {
      console.error("Failed to fetch matches", error);
      setSearchResults([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileClaim = () => {
    setIsFlipped(true);
  };

  const handleSubmitClaim = async () => {
    try {
      const bestMatch = searchResults.find(result => result.final_score === Math.max(...searchResults.map(r => r.final_score)));
      
      // Get Firebase token
      const user = auth.currentUser;
      if (!user) {
        alert("Please login to submit a claim");
        navigate("/auth");
        return;
      }
      
      const token = await user.getIdToken();
      
      const claimData = {
        full_name: formData.fullName,
        email: formData.email,
        contact_number: formData.contactNumber,
        item_description: formData.description,
        additional_notes: formData.additionalNotes || "",
        consent: formData.consent,
        found_item_id: bestMatch?.id || null  // Link to the matched item
      };
      
      console.log("Form Data:", formData);
      console.log("Submitting claim:", claimData);
      
      // Submit claim to backend
      const response = await fetch(getApiUrl("api/claims/submit"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(claimData)
      });
      
      const data = await response.json();
      if (!response.ok) {
        console.error("Server error response:", data);
        const errorMsg = data.message || JSON.stringify(data.detail) || "Failed to submit claim";
        throw new Error(errorMsg);
      }
      // Build QR payload for ClaimReceipt (QR generation handled there)
      const payload = {
        type: "claim",
        ref: data.reference || `UF-CLM-${data.claim_id}`,
        claim_id: data.claim_id,
        found_item_id: claimData.found_item_id || null,
        email: claimData.email,
        submitted_at: data.claim_date || new Date().toISOString(),
      };
      setQrPayload(payload);
      setClaimSubmitted(true);
    } catch (error) {
      console.error("Error submitting claim:", error);
      alert("Error submitting claim: " + error.message);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'consent' ? event.target.checked : event.target.value
    }));
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#FBFFE9",
        minHeight: isMobile ? "100dvh" : "100vh", // Use dynamic viewport height for mobile
        height: isMobile ? "100dvh" : "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen && !isMobile ? "300px" : 0,
        maxWidth: isSidebarOpen && !isMobile ? "calc(100vw - 300px)" : "100vw",
        overflowX: "hidden",
        overflowY: isMobile ? "hidden" : "auto",
      }}
    >
      <Header />
      <Container 
        sx={{ 
          textAlign: "center", 
          mt: isSearching ? 5 : (isMobile ? 2 : 30),
          mb: 0,
          transition: "margin-top 0.3s ease-in-out",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          justifyContent: isMobile && !isSearching ? "flex-end" : "flex-start",
          px: isMobile ? 2 : 3,
          pb: isMobile && !isSearching ? 2 : 0,
          height: isMobile ? "100%" : "auto",
          overflow: isMobile && !isSearching ? "hidden" : "auto",
        }}
      >
        {/* Content area */}
        <Box sx={{ 
          flex: isMobile && !isSearching ? 0 : 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: isMobile && !isSearching ? "flex-end" : "flex-start",
        }}>
          {!isSearching && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#516d4f",
                mb: isMobile ? 2 : 2,
                fontFamily: "Poppins, Helvetica",
                fontSize: isMobile ? "20px" : "24px",
                whiteSpace: "nowrap",
              }}
            >
              {displayText}
            </Typography>
          )}

          {/* Desktop layout - keep original order */}
          {!isMobile && (
            <>
              <SearchBar 
                searchValue={searchValue}
                onInputChange={handleSearchInputChange}
                onSend={handleSend}
                inputRef={inputRef}
                numLines={numLines}
                isMobile={isMobile}
                fileInputRef={fileInputRef}
                onFileChange={handleFileChange}
                onAttachClick={handleAttachClick}
                imagePreview={imagePreview}
                onRemoveImage={handleRemoveImage}
              />

              {!isSearching && suggestions.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    width: "100%",
                    maxWidth: "800px",
                    mx: "auto",
                    bgcolor: "#f4f4f4",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  {suggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        display: "block",
                        textAlign: "left",
                        width: "100%",
                        padding: "10px",
                        borderBottom: "1px solid #ccc",
                        color: "#516d4f",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </Box>
              )}

              {!isSearching && (
                <SearchTips isMobile={isMobile} />
              )}
            </>
          )}
          {/* Mobile layout - Tips above search bar */}
          {isMobile && !isSearching && (
            <SearchTips isMobile={isMobile} />
          )}

          {isProcessing && <ProcessingView />}
          {!isProcessing && searchResults !== null && (
            <ResultView 
              searchResults={searchResults}
              isFlipped={isFlipped}
              onFileClaim={handleFileClaim}
              formData={formData}
              onInputChange={handleInputChange}
              onBackToMatch={() => setIsFlipped(false)}
              onSubmitClaim={handleSubmitClaim}
              qrPayload={qrPayload}
              onGoToHistory={() => {
                // optional: reset after download or navigation
                setFormData({ fullName: "", email: "", contactNumber: "", description: "", additionalNotes: "", consent: false });
                setIsFlipped(true);
                navigate("/user/search-history");
              }}
              isMobile={isMobile}
            />
          )}
        </Box>

        {/* Mobile - Search bar at bottom */}
        {isMobile && !isSearching && (
          <Box sx={{ 
            pb: 2,
            pt: 0,
            position: "sticky",
            bottom: 0,
            bgcolor: "#FBFFE9",
            zIndex: 10,
          }}>
            <SearchBar 
              searchValue={searchValue}
              onInputChange={handleSearchInputChange}
              onSend={handleSend}
              inputRef={inputRef}
              numLines={numLines}
              isMobile={isMobile}
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              onAttachClick={handleAttachClick}
              imagePreview={imagePreview}
              onRemoveImage={handleRemoveImage}
            />

            {suggestions.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  width: "100%",
                  maxWidth: "800px",
                  mx: "auto",
                  bgcolor: "#f4f4f4",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                {suggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={{
                      display: "block",
                      textAlign: "left",
                      width: "100%",
                      padding: "10px",
                      borderBottom: "1px solid #ccc",
                      color: "#516d4f",
                      "&:last-child": {
                        borderBottom: "none",
                      },
                    }}
                  >
                    {suggestion}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Container>
      {/* QR shown inline in the claim form via ResultView/ClaimForm */}
    </Box>
  );
};

export default Home;